import { InfiniteData } from '@tanstack/react-query';

/**
 * Generic функция для конвертации InfiniteQuery данных в плоский массив
 *
 * @template TResponse - Тип ответа сервера (например, ListOfferResponse.AsObject)
 * @template TItem - Тип элемента в списке (например, Offer.AsObject)
 *
 * @param data - InfiniteData из useInfiniteQuery
 * @param listKey - Ключ массива в response ('itemsList', 'usersList', 'dealsList' и т.д.)
 * @param transform - Опциональная функция трансформации массива элементов
 *
 * @returns Плоский массив элементов типа TItem
 *
 * @example
 * // Базовое использование
 * const items = convertInfiniteDataToArray<ListOfferResponse.AsObject, Offer.AsObject>(
 *   data,
 *   'itemsList'
 * );
 *
 * @example
 * // С трансформацией
 * const items = convertInfiniteDataToArray<ListPricingResponse.AsObject, Pricing.AsObject>(
 *   data,
 *   'itemsList',
 *   (items) => items.map((el, index) => ({ ...el, externalId: index + 1 }))
 * );
 */
export function convertInfiniteDataToArray<TResponse, TItem>(
  data: InfiniteData<TResponse> | undefined,
  listKey: keyof TResponse,
  transform?: (items: TItem[]) => TItem[]
): TItem[] {
  if (!data) {
    return [];
  }

  const flatItems = data.pages.reduce(
    (acc: TItem[], page) => {
      const pageItems = (page[listKey] as unknown as TItem[]) || [];
      return [...acc, ...pageItems];
    },
    []
  );

  return transform ? transform(flatItems) : flatItems;
}

/**
 * Generic функция для получения общего количества элементов из InfiniteQuery данных
 *
 * @template TResponse - Тип ответа сервера с полем pagination
 *
 * @param data - InfiniteData из useInfiniteQuery
 * @returns Общее количество элементов или 0
 *
 * @example
 * const totalCount = getTotalCountFromInfiniteData(data);
 */
export function getTotalCountFromInfiniteData<
  TResponse extends { pagination?: { totalItems?: number } }
>(data: InfiniteData<TResponse> | undefined): number {
  if (!data) {
    return 0;
  }

  const [firstPage] = data.pages;
  return firstPage?.pagination?.totalItems || 0;
}

/**
 * Стандартная логика getNextPageParam для пагинации
 *
 * @template TResponse - Тип ответа сервера с полем pagination
 *
 * @param lastPage - Последняя загруженная страница
 * @param allPages - Все загруженные страницы
 * @returns Номер следующей страницы или undefined если страницы закончились
 *
 * @example
 * useInfiniteQuery({
 *   // ...
 *   getNextPageParam: getStandardNextPageParam
 * })
 */
export function getStandardNextPageParam<
  TResponse extends { pagination?: { totalPages?: number } }
>(lastPage: TResponse, allPages: TResponse[]): number | undefined {
  const totalPages = lastPage?.pagination?.totalPages || 0;
  return totalPages > allPages.length ? allPages.length + 1 : undefined;
}